import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, Zap, CheckCircle2, AlertCircle, FileText, ArrowRight } from 'lucide-react';
import { useStore } from '../store';
import { selectTemplate, matchMachines, MATERIALS, PartInput } from '../engine/rules';

const MATS = Object.keys(MATERIALS);
const HEAD_TYPES = ['HEX_SOCKET','EXTERNAL_HEX','HEX_FLANGE','PLAIN_HEX','DRAIN_PLUG','NONE'];

export default function NewJob() {
  const navigate = useNavigate();
  const addJob = useStore(s => s.addJob);
  const [step, setStep] = useState<1|2|3>(1);
  const [file, setFile] = useState<File|null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState('');
  const [f, setF] = useState({
    customer:'', part_no:'', description:'',
    part_type:'BOLT', nominal_diameter_mm:'10', thread_pitch_mm:'1.5',
    total_length_mm:'30', shank_length_mm:'', head_type:'HEX_SOCKET',
    socket_type:'HEX_SOCKET', has_flange:false, has_serration:false,
    has_nylon_insert:false, nut_height_mm:'', nut_af_mm:'',
    wire_diameter_mm:'', blank_length_mm:'', weight_grams:'',
    material_id:'SAE1010', engineer_notes:'',
  });
  const upd = (k:string, v:any) => setF(p => ({...p, [k]:v}));

  const toPart = (): PartInput => ({
    part_type: f.part_type as any,
    nominal_diameter_mm: +f.nominal_diameter_mm||10,
    thread_pitch_mm: +f.thread_pitch_mm||1.5,
    total_length_mm: +f.total_length_mm||30,
    shank_length_mm: f.shank_length_mm ? +f.shank_length_mm : undefined,
    head_type: f.head_type, socket_type: f.socket_type,
    has_flange: f.has_flange, has_serration: f.has_serration,
    has_nylon_insert: f.has_nylon_insert,
    nut_height_mm: f.nut_height_mm ? +f.nut_height_mm : undefined,
    nut_af_mm: f.nut_af_mm ? +f.nut_af_mm : undefined,
    wire_diameter_mm: f.wire_diameter_mm ? +f.wire_diameter_mm : undefined,
    blank_length_mm: f.blank_length_mm ? +f.blank_length_mm : undefined,
    weight_grams: f.weight_grams ? +f.weight_grams : undefined,
    material_id: f.material_id,
  });

  const handleDrop = useCallback((e:React.DragEvent) => {
    e.preventDefault(); setDragOver(false);
    const fl = e.dataTransfer.files[0];
    if (fl) setFile(fl);
  }, []);

  const parseWithAI = async () => {
    if (!file) return;
    setParsing(true); setParseError('');
    try {
      const b64 = await new Promise<string>((res,rej) => {
        const r = new FileReader();
        r.onload = () => res((r.result as string).split(',')[1]);
        r.onerror = rej; r.readAsDataURL(file);
      });
      const isImg = file.type.startsWith('image/');
      const resp = await fetch('https://api.anthropic.com/v1/messages', {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({
          model:'claude-sonnet-4-20250514', max_tokens:1000,
          messages:[{ role:'user', content:[
            { type: isImg ? 'image' : 'document',
              source:{ type:'base64', media_type:file.type, data:b64 }},
            { type:'text', text:`You are ForgeIQ drawing parser for Sturdfix cold forging, Ludhiana.
Extract all fields and return ONLY a JSON object (no markdown):
{"part_no":"","customer":"","description":"","part_type":"BOLT|NUT",
"nominal_diameter_mm":0,"thread_pitch_mm":0,"total_length_mm":0,
"shank_length_mm":0,"head_type":"HEX_SOCKET|EXTERNAL_HEX|HEX_FLANGE|PLAIN_HEX|DRAIN_PLUG|NONE",
"socket_type":"HEX_SOCKET|NONE","has_flange":false,"has_serration":false,"has_nylon_insert":false,
"nut_height_mm":0,"nut_af_mm":0,"wire_diameter_mm":0,"blank_length_mm":0,"weight_grams":0,
"material_id":"10B30|10B33|10B21|15B25|SAE1008|SAE1010|SAE1022|SAE1035|SCM435|EN8D|A194_2H",
"confidence":0.0}
Use null for missing. Convert inches to mm if needed.` }
          ]}]
        })
      });
      const data = await resp.json();
      const text = data.content?.map((c:any)=>c.text||'').join('');
      const p = JSON.parse(text.replace(/```json|```/g,'').trim());
      Object.keys(p).forEach(k => { if (p[k] != null && k in f) upd(k, typeof p[k]==='boolean' ? p[k] : String(p[k])); });
      setStep(2);
    } catch(e:any) {
      setParseError('Parse failed — please fill in manually.');
      setStep(2);
    } finally { setParsing(false); }
  };

  const preview = f.nominal_diameter_mm ? (() => {
    const p = toPart();
    const { template, confidence, reasoning } = selectTemplate(p);
    const machines = matchMachines(p, template).filter((m:any) => m.eligible);
    return { template, confidence, reasoning, machines };
  })() : null;

  const handleCreate = () => {
    const part = toPart();
    const { template } = selectTemplate(part);
    const machines = matchMachines(part, template);
    const bestMachine = machines.find((m:any) => m.eligible);
    const id = addJob({
      status:'REVIEW', customer:f.customer, part_no:f.part_no,
      description:f.description, drawing_filename:file?.name,
      part, template, selected_machine:bestMachine?.machine_id,
      engineer_notes:f.engineer_notes,
    });
    navigate(`/jobs/${id}`);
  };

  const stepDone = (n:number) => (n === 1 ? !!file : n === 2 ? !!f.nominal_diameter_mm && !!f.customer : true);

  return (
    <div className="fade-in" style={{ maxWidth: 760 }}>
      <div style={{ marginBottom: 24 }}>
        <h1>New Process Planning Job</h1>
        <p style={{ marginTop:4, fontSize:13 }}>Upload a drawing for AI extraction, or enter dimensions manually</p>
      </div>

      {/* Step indicator */}
      <div style={{ display:'flex', alignItems:'center', gap:0, marginBottom:28 }}>
        {[{n:1,l:'Upload Drawing'},{n:2,l:'Part Dimensions'},{n:3,l:'Review & Create'}].map(({n,l},i) => (
          <React.Fragment key={n}>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <div style={{
                width:28, height:28, borderRadius:'50%',
                background: step > n ? 'var(--c-green)' : step === n ? 'var(--c-navy)' : 'var(--c-surface3)',
                color: step >= n ? '#fff' : 'var(--t-faint)',
                display:'flex', alignItems:'center', justifyContent:'center',
                fontSize:12, fontWeight:600, flexShrink:0,
              }}>
                {step > n ? <CheckCircle2 size={14} /> : n}
              </div>
              <span style={{ fontSize:13, fontWeight: step===n?600:400, color: step>=n?'var(--t-primary)':'var(--t-faint)' }}>{l}</span>
            </div>
            {i < 2 && <div style={{ flex:1, height:1, background:'var(--c-border)', margin:'0 14px' }} />}
          </React.Fragment>
        ))}
      </div>

      {/* STEP 1 */}
      {step === 1 && (
        <div className="card card-pad">
          <h3 style={{ marginBottom:16 }}>Upload Engineering Drawing</h3>
          <div className={`upload-zone ${dragOver?'drag-over':''}`}
            onDragOver={e=>{e.preventDefault();setDragOver(true)}}
            onDragLeave={()=>setDragOver(false)}
            onDrop={handleDrop}
            onClick={()=>document.getElementById('fi')?.click()}>
            <input id="fi" type="file" accept=".pdf,image/*" style={{display:'none'}}
              onChange={e=>e.target.files?.[0]&&setFile(e.target.files[0])} />
            {file ? (
              <div>
                <CheckCircle2 size={36} color="var(--c-green)" style={{margin:'0 auto 10px',display:'block'}} />
                <p style={{fontWeight:600,color:'var(--t-primary)'}}>{file.name}</p>
                <p style={{color:'var(--t-muted)',fontSize:12,marginTop:4}}>{(file.size/1024).toFixed(0)} KB · Click to change</p>
              </div>
            ) : (
              <div>
                <Upload size={36} color="var(--t-faint)" style={{margin:'0 auto 12px',display:'block'}} />
                <p style={{fontWeight:500,color:'var(--t-secondary)'}}>Drag PDF or image here</p>
                <p style={{color:'var(--t-muted)',fontSize:12,marginTop:4}}>Supports DXF screenshots, scanned drawings, PDF</p>
              </div>
            )}
          </div>
          {parseError && <div className="alert alert-error" style={{marginTop:12}}><AlertCircle size={14}/>{parseError}</div>}
          <div style={{display:'flex',gap:10,marginTop:16}}>
            {file && !parsing && <button className="btn btn-navy" onClick={parseWithAI}><Zap size={13}/>Parse with AI</button>}
            {parsing && <button className="btn btn-navy" disabled><span className="spinner" style={{width:12,height:12}}/>Parsing drawing…</button>}
            <button className="btn btn-secondary" onClick={()=>setStep(2)}>Enter manually <ArrowRight size={12}/></button>
          </div>
        </div>
      )}

      {/* STEP 2 */}
      {step === 2 && (
        <div className="card card-pad">
          <h3 style={{ marginBottom:20 }}>Part Dimensions</h3>
          <div className="form-row" style={{marginBottom:0}}>
            <div className="form-group"><label>Customer *</label>
              <input className="input" value={f.customer} onChange={e=>upd('customer',e.target.value)} placeholder="e.g. APA Engineering" /></div>
            <div className="form-group"><label>Part Number</label>
              <input className="input" value={f.part_no} onChange={e=>upd('part_no',e.target.value)} placeholder="Customer part number" /></div>
          </div>
          <div className="form-group"><label>Description</label>
            <input className="input" value={f.description} onChange={e=>upd('description',e.target.value)} placeholder="e.g. M12×1.75×80 Hex Socket Head Cap Screw Grade 10.9" /></div>
          <div style={{borderTop:'1px solid var(--c-border)',margin:'16px 0'}}/>
          <div className="form-row">
            <div className="form-group"><label>Part Type</label>
              <select className="select" value={f.part_type} onChange={e=>upd('part_type',e.target.value)}>
                <option value="BOLT">BOLT</option><option value="NUT">NUT</option>
              </select></div>
            <div className="form-group"><label>Material</label>
              <select className="select" value={f.material_id} onChange={e=>upd('material_id',e.target.value)}>
                {MATS.map(m=><option key={m} value={m}>{m} — {MATERIALS[m].application}</option>)}
              </select></div>
          </div>
          <div className="form-row-3">
            <div className="form-group"><label>Nominal Ø (mm) *</label>
              <input className="input" type="number" value={f.nominal_diameter_mm} onChange={e=>upd('nominal_diameter_mm',e.target.value)} /></div>
            <div className="form-group"><label>Pitch (mm)</label>
              <input className="input" type="number" value={f.thread_pitch_mm} onChange={e=>upd('thread_pitch_mm',e.target.value)} /></div>
            <div className="form-group"><label>Total Length (mm)</label>
              <input className="input" type="number" value={f.total_length_mm} onChange={e=>upd('total_length_mm',e.target.value)} /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Wire Ø (mm)</label>
              <input className="input" type="number" value={f.wire_diameter_mm} onChange={e=>upd('wire_diameter_mm',e.target.value)} placeholder="Auto if blank" /></div>
            <div className="form-group"><label>Blank Length (mm)</label>
              <input className="input" type="number" value={f.blank_length_mm} onChange={e=>upd('blank_length_mm',e.target.value)} placeholder="Auto if blank" /></div>
          </div>
          {f.part_type === 'BOLT' && <>
            <div className="form-row">
              <div className="form-group"><label>Head Type</label>
                <select className="select" value={f.head_type} onChange={e=>upd('head_type',e.target.value)}>
                  {HEAD_TYPES.map(h=><option key={h} value={h}>{h.replace(/_/g,' ')}</option>)}
                </select></div>
              <div className="form-group"><label>Shank Length (mm)</label>
                <input className="input" type="number" value={f.shank_length_mm} onChange={e=>upd('shank_length_mm',e.target.value)} /></div>
            </div>
            <div style={{display:'flex',gap:24,marginBottom:14}}>
              {[['has_flange','Has Flange'],['has_serration','Has Serration']].map(([k,l])=>(
                <label key={k} style={{display:'flex',alignItems:'center',gap:7,textTransform:'none',fontSize:13,color:'var(--t-secondary)',marginBottom:0,cursor:'pointer',fontWeight:400}}>
                  <input type="checkbox" checked={(f as any)[k]} onChange={e=>upd(k,e.target.checked)} style={{width:14,height:14}} />{l}
                </label>
              ))}
            </div>
          </>}
          {f.part_type === 'NUT' && <div className="form-row">
            <div className="form-group"><label>Nut Height (mm)</label>
              <input className="input" type="number" value={f.nut_height_mm} onChange={e=>upd('nut_height_mm',e.target.value)} /></div>
            <div className="form-group"><label>A/F (mm)</label>
              <input className="input" type="number" value={f.nut_af_mm} onChange={e=>upd('nut_af_mm',e.target.value)} /></div>
          </div>}

          {/* Live AI preview */}
          {preview && (
            <div style={{background:'var(--c-blue-lt)',border:'1px solid var(--c-blue-mid)',borderRadius:8,padding:'12px 16px',marginTop:8}}>
              <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:8}}>
                <Zap size={13} color="var(--c-blue)" />
                <span style={{fontSize:12,fontWeight:600,color:'var(--c-blue)'}}>Live Analysis</span>
                <span className={`badge ${preview.confidence > 0.85 ? 'badge-green' : 'badge-amber'}`}>{(preview.confidence*100).toFixed(0)}% confidence</span>
              </div>
              <div style={{fontSize:14,fontWeight:700,color:'var(--c-navy)',marginBottom:4}}>
                Template {preview.template} — {preview.reasoning[preview.reasoning.length-1]}
              </div>
              {preview.machines.slice(0,3).map((m:any)=>(
                <span key={m.machine_id} className="badge badge-navy" style={{marginRight:6}}>{m.name}</span>
              ))}
            </div>
          )}

          <div style={{display:'flex',gap:10,marginTop:20}}>
            <button className="btn btn-secondary" onClick={()=>setStep(1)}>← Back</button>
            <button className="btn btn-navy" onClick={()=>setStep(3)} disabled={!f.nominal_diameter_mm||!f.customer}>
              Review <ArrowRight size={12}/>
            </button>
          </div>
        </div>
      )}

      {/* STEP 3 */}
      {step === 3 && (() => {
        const part = toPart();
        const { template, confidence } = selectTemplate(part);
        const machines = matchMachines(part, template).filter((m:any)=>m.eligible);
        return (
          <div className="card card-pad">
            <h3 style={{marginBottom:20}}>Review & Confirm</h3>
            <div style={{background:'var(--c-surface2)',border:'1px solid var(--c-border)',borderRadius:8,padding:16,marginBottom:20}}>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                {[['Part',`${f.part_no||'—'} · ${f.description||'—'}`],['Customer',f.customer],
                  ['Template',`Template ${template}`],['Confidence',`${(confidence*100).toFixed(0)}%`],
                  ['Eligible machines',machines.length.toString()],['Material',f.material_id]
                ].map(([k,v])=>(
                  <div key={k} style={{display:'flex',justifyContent:'space-between',padding:'6px 0',borderBottom:'1px solid var(--c-border)',fontSize:13}}>
                    <span style={{color:'var(--t-muted)'}}>{k}</span>
                    <span style={{fontWeight:500,color:'var(--t-primary)'}}>{v}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="form-group"><label>Engineer Notes</label>
              <textarea className="textarea" value={f.engineer_notes} onChange={e=>upd('engineer_notes',e.target.value)}
                placeholder="Special requirements, customer callouts, process notes…" /></div>
            <div style={{display:'flex',gap:10,marginTop:8}}>
              <button className="btn btn-secondary" onClick={()=>setStep(2)}>← Back</button>
              <button className="btn btn-navy btn-lg" onClick={handleCreate}>
                <Zap size={14}/> Create Job & Generate Sequence
              </button>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
